import React from 'react';
import { ErrorMessage } from '@atlaskit/form/error-message';
import Field from '@atlaskit/form/field';
import { HelperMessage } from '@atlaskit/form/helper-message';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { CheckboxSelect } from '@atlaskit/select/checkbox-select';
import type { ValueType } from '@atlaskit/select/types';
import Textfield from '@atlaskit/textfield/text-field';
import { useIntl, defineMessage } from '@jira/platform__react-intl';
import { THIS_PRODUCT_NAME_STRING } from './applinksListPageUrl.constants.tsx';
import CopyButton from './CopyButton.tsx';
import { validateSupportedScopes, validateURLField } from './formatBackendError.tsx';
import type { SelectOption } from './StageProps.types.tsx';

const clientIdLabel = defineMessage({
	defaultMessage: 'App client ID',
	id: 'applinks.administration.ui.common.fields.client-id.label',
	description:
		'Label for the Client ID field. Also used in Copy button tooltip i.e. Copy "App Client ID".',
});

const clientSecretLabel = defineMessage({
	defaultMessage: 'App client secret',
	id: 'applinks.administration.ui.common.fields.client-secret.label',
	description:
		'Label for the Client Secret field. Also used in Copy button tooltip i.e. Copy "App Client Secret".',
});

const remoteApplicationScopesLabel = defineMessage({
	defaultMessage: 'Remote app scopes',
	id: 'applinks.administration.ui.common.fields.remote-application-scopes.label',
	description:
		'Label for the Remote App Scopes drop-down. The "Application" terminology should match Application URL',
});

const remoteApplicationScopesPlaceholder = defineMessage({
	defaultMessage: 'Use the same scopes as the remote app',
	id: 'applinks.administration.ui.common.fields.remote-application-scopes.placeholder',
	description:
		'Placeholder text for the Remote app Scopes drop-down, instructing the user which items to select',
});

const redirectUrlLabel = defineMessage({
	defaultMessage: 'Redirect URL',
	id: 'applinks.administration.ui.common.fields.redirect-url.label',
	description:
		'The text is shown as a label for the Redirect URL input field in the application link configuration form, where users enter or view the OAuth redirect URL.',
});

const displayNameLabel = defineMessage({
	defaultMessage: 'Display name',
	id: 'applinks.administration.ui.common.fields.display-name.label',
	description:
		'The text is shown as a label for the Display Name input field in the application link configuration form, where users enter a human-readable name for the application link.',
});

const displayNamePlaceholder = defineMessage({
	defaultMessage: 'Name for the application link',
	id: 'applinks.administration.ui.common.fields.display-name.placeholder',
	description: 'Placeholder text for the Display Name field.',
});

const displayNameHelperMessage = defineMessage({
	defaultMessage: 'Enter a unique name for the link, for example the name of your external app.',
	id: 'applinks.administration.ui.common.fields.display-name.helper-message',
	description: 'Helpful message displayed below the Display Name field.',
});

const displayUrlLabel = defineMessage({
	defaultMessage: 'Display URL',
	id: 'applinks.administration.ui.common.fields.display-url.label',
	description:
		'The text is shown as a label for the Display URL input field in the application link configuration form, where users enter the URL displayed to end users for the linked application.',
});

const displayUrlPlaceholder = defineMessage({
	defaultMessage: 'URL for the application link',
	id: 'applinks.administration.ui.common.fields.display-url.placeholder',
	description: 'Placeholder text for the Display URL field.',
});

const displayUrlHelperMessage = defineMessage({
	// eslint-disable-next-line @atlassian/i18n/no-multiple-whitespaces
	defaultMessage: "This is the URL that we'll display to users in  {productName}.",
	id: 'applinks.administration.ui.common.fields.display-url.helper-message',
	description:
		'Helpful message displayed below the Display URL field. Product Name is the current product i.e. "Jira"',
});

const applicationUrlLabel = defineMessage({
	defaultMessage: 'Application URL',
	id: 'applinks.administration.ui.common.fields.application-url.label',
	description:
		'Label for the Application URL field. The "Application" terminology should match Application Scopes',
});

const applicationUrlPlaceholder = defineMessage({
	defaultMessage: 'Application URL for the application link',
	id: 'applinks.administration.ui.common.fields.application-url.placeholder',
	description: 'Placeholder text for the Application URL field.',
});

const applicationUrlHelperMessage = defineMessage({
	defaultMessage:
		'This is the URL that {productName} will use to communicate with the external app.',
	id: 'applinks.administration.ui.common.fields.application-url.helper-message',
	description:
		'Helpful message displayed below the Application URL field. Product Name is the current product i.e. "Jira"',
});

export const CopyableApplicationClientID = (props: {
	name: string;
	clientId: string;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Field
			name={props.name}
			defaultValue={props.clientId}
			label={formatMessage(clientIdLabel)}
			isDisabled
		>
			{({ fieldProps }) => (
				<Textfield
					testId="applinks-administration.ui.common.fields.copyable-application-client-id"
					isReadOnly
					type="password"
					{...fieldProps}
					elemAfterInput={
						<CopyButton label={formatMessage(clientIdLabel)} value={props.clientId} />
					}
				/>
			)}
		</Field>
	);
};

export const CopyableApplicationClientSecret = (props: {
	name: string;
	clientSecret: string;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Field
			name={props.name}
			defaultValue={props.clientSecret}
			label={formatMessage(clientSecretLabel)}
			isDisabled
		>
			{({ fieldProps }) => (
				<Textfield
					testId="applinks-administration.ui.common.fields.copyable-application-client-secret"
					isReadOnly
					type="password"
					{...fieldProps}
					elemAfterInput={
						<CopyButton label={formatMessage(clientSecretLabel)} value={props.clientSecret} />
					}
				/>
			)}
		</Field>
	);
};

export const RemoteApplicationScopeMultiSelect = (props: {
	name: string;
	supportedScopes: string[];
	defaultSelected?: ValueType<SelectOption, true>;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Field<ValueType<SelectOption, true>>
			name={props.name}
			label={formatMessage(remoteApplicationScopesLabel)}
			defaultValue={props.defaultSelected}
			validate={(chosenScopes) =>
				validateSupportedScopes(
					chosenScopes ? chosenScopes.map((scope) => scope.value) : [],
					props.supportedScopes,
				)
			}
			isRequired
		>
			{({ fieldProps, error }) => (
				<Stack>
					<CheckboxSelect
						testId="applinks-administration.ui.common.fields.remote-application-scope-multi-select"
						placeholder={formatMessage(remoteApplicationScopesPlaceholder)}
						{...fieldProps}
						options={props.supportedScopes.map((scope) => ({
							label: scope,
							value: scope,
						}))}
					/>
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</Stack>
			)}
		</Field>
	);
};

export const ApplicationClientId = (props: {
	name: string;
	isRequired?: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Field name={props.name} label={formatMessage(clientIdLabel)} isRequired={props.isRequired}>
			{({ fieldProps }) => (
				<Textfield
					testId="applinks-administration.ui.common.fields.application-client-id"
					type="password"
					placeholder={formatMessage(clientIdLabel)}
					{...fieldProps}
				/>
			)}
		</Field>
	);
};

export const ApplicationClientSecret = (props: {
	name: string;
	isRequired?: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Field name={props.name} label={formatMessage(clientSecretLabel)} isRequired={props.isRequired}>
			{({ fieldProps }) => (
				<Textfield
					testId="applinks-administration.ui.common.fields.application-client-secret"
					type="password"
					placeholder={formatMessage(clientSecretLabel)}
					{...fieldProps}
				/>
			)}
		</Field>
	);
};

export const RedirectUrl = (props: {
	name: string;
	defaultRedirectUrl?: string;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Field
			name={props.name}
			defaultValue={props.defaultRedirectUrl}
			label={formatMessage(redirectUrlLabel)}
			validate={(redirectUrl) => validateURLField(redirectUrl, true, true)}
			isRequired
		>
			{({ fieldProps, error }) => (
				<>
					<Textfield
						testId="applinks-administration.ui.common.fields.redirect-url"
						placeholder={formatMessage(redirectUrlLabel)}
						{...fieldProps}
					/>
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</>
			)}
		</Field>
	);
};

export const DisplayName = (props: { name: string; defaultName: string }): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Field
			name={props.name}
			defaultValue={props.defaultName}
			label={formatMessage(displayNameLabel)}
			isRequired
		>
			{({ fieldProps }) => (
				<Stack>
					<Textfield
						testId="applinks-administration.ui.common.fields.display-name"
						placeholder={formatMessage(displayNamePlaceholder)}
						{...fieldProps}
					/>
					<HelperMessage>{formatMessage(displayNameHelperMessage)}</HelperMessage>
				</Stack>
			)}
		</Field>
	);
};

export const DisplayUrl = (props: { name: string; defaultUrl: string }): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Field
			name={props.name}
			defaultValue={props.defaultUrl}
			label={formatMessage(displayUrlLabel)}
			isRequired
			validate={(defaultUrl) => validateURLField(defaultUrl, true, false)}
		>
			{({ fieldProps, error }) => (
				<Stack>
					<Textfield
						testId="applinks-administration.ui.common.fields.display-url"
						placeholder={formatMessage(displayUrlPlaceholder)}
						{...fieldProps}
					/>
					{!error && (
						<HelperMessage>
							{formatMessage(displayUrlHelperMessage, {
								productName: THIS_PRODUCT_NAME_STRING,
							})}
						</HelperMessage>
					)}
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</Stack>
			)}
		</Field>
	);
};

export const ApplicationUrl = (props: {
	name: string;
	defaultUrl: string;
	disabled: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Field
			name={props.name}
			defaultValue={props.defaultUrl}
			label={formatMessage(applicationUrlLabel)}
			isDisabled={props.disabled}
			isRequired={!props.disabled}
			validate={(redirectUrl) => validateURLField(redirectUrl, true, true)}
		>
			{({ fieldProps, error }) => (
				<Stack>
					<Textfield
						testId="applinks-administration.ui.common.fields.application-url"
						placeholder={formatMessage(applicationUrlPlaceholder)}
						{...fieldProps}
					/>
					{!error && (
						<HelperMessage>
							{formatMessage(applicationUrlHelperMessage, {
								productName: THIS_PRODUCT_NAME_STRING,
							})}
						</HelperMessage>
					)}
					{error && <ErrorMessage>{error}</ErrorMessage>}
				</Stack>
			)}
		</Field>
	);
};
